import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PlaylistCard } from './PlaylistCard';
import { useFeaturedPlaylists } from '../hooks/useFeaturedPlaylists';
import { SubmissionModal } from './SubmissionModal';
import { Toast } from './Toast';

interface GenreFeaturedPlaylistsProps {
  genre: string;
  subGenre?: string;
  totalCount: number;
  onViewAll?: () => void;
}

export function GenreFeaturedPlaylists({ genre, subGenre, totalCount, onViewAll }: GenreFeaturedPlaylistsProps) {
  const { playlists, loading } = useFeaturedPlaylists(8, genre, subGenre);
  const [selectedPlaylist, setSelectedPlaylist] = useState<typeof playlists[0] | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  if (loading || playlists.length === 0) return null;

  const date = new Date();
  const formattedDate = date.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric' 
  });

  const title = subGenre 
    ? `Today's Featured ${subGenre} ${genre} Playlists - ${formattedDate}`
    : `Today's Featured ${genre} Playlists - ${formattedDate}`;

  // Create the search params for the playlists page
  const searchParams = new URLSearchParams();
  searchParams.set('genre', genre);
  if (subGenre) {
    searchParams.set('subGenre', subGenre);
  }

  return (
    <>
      <div className="border-l-4 border-green-500 pl-6 mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">{title}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {playlists.map((playlist) => (
            <PlaylistCard
              key={playlist.id}
              playlist={playlist}
              onSubmit={(id) => setSelectedPlaylist(playlists.find(p => p.id === id) || null)}
              priority={true}
              showFeaturedBadge={true}
            />
          ))}
        </div>
      </div>

      <div className="text-center mb-12">
        <button
          onClick={onViewAll}
          className="inline-block px-8 py-4 bg-green-500 text-white text-lg font-semibold rounded-lg hover:bg-green-600 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
        >
          View All {totalCount.toLocaleString()} {subGenre ? `${subGenre} ${genre}` : genre} Playlists
        </button>
      </div>

      {selectedPlaylist && (
        <SubmissionModal
          playlist={selectedPlaylist}
          onClose={() => setSelectedPlaylist(null)}
          onSubmit={() => {
            setSelectedPlaylist(null);
            setToast({
              type: 'success',
              message: `Track submitted to ${selectedPlaylist.name}! Watch your inbox for feedback from the curator.`
            });
          }}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
} 